import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { AzElRangeGA2, AzelRange, PERMISSION_ERROR_MESSAGE, Status } from 'common/utils/constants';
import { getDefaultHeatmapRange } from 'common/utils/helpers';
import { IconLoader } from 'common/IconLoader';
import { updateSingleDevicePageSelectedRasterRecord, updateSingleDevicePageSelectedRasterId } from 'redux/actions';
import { IconAndText } from 'components/shared/SvgAF/SvgAF';
import Modal from 'components/shared/Modal/Modal';
import RasterHistoryHeatmap from '../RasterHeatmap/RasterHistoryHeatmap/RasterHistoryHeatmap';
import HeatmapLibraryTable from '../HeatmapLibraryTable/HeatmapLibraryTable';
import RasterHeatmapSlider from '../RasterHeatmap/RasterHeatmapSlider/RasterHeatmapSlider';
import HeatmapExportableImagesContainer from './HeatmapExportableImagesContainer/HeatmapExportableImagesContainer';
import HeatmapExportDropdown from './HeatmapExportDropdown/HeatmapExportDropdown';
import { csvDataParser, mapRecord } from './utils/helpers';
import './HeatmapViewAndExportModal.css';

function HeatmapViewAndExportModal({
  donorDevice,
  isGa2,
  shouldFetchHeatmapRecords,
  fetchRasterRecordsForDevice,
  setShouldFetchHeatmapRecords,
  onClose,
  setIsHeatmapRetrieveModalOpen,
  setIsCollectingFiles
}) {
  const timeoutRef = useRef(null);
  const dispatch = useDispatch();
  const permissions = useSelector((store) => store.data.rolePermissions?.permissions);
  const hasCreateRasterHeatmapPermission = permissions && permissions.includes('cm_create_raster_heatmap');
  const selectedDeviceHeatmapRasterId = useSelector((store) => store.ui.singleDevicePage?.heatmap?.selectedRasterId);
  const selectedRastersDictionary = useSelector((store) => store.ui.singleDevicePage.heatmap.selectedRastersDictionary);
  const deviceHeatmapHistoryDictionary = useSelector((store) => store.data.heatmapHistoryDictionary?.[donorDevice]);
  const selectedDeviceHeatmapRecord = useSelector((store) => store.ui.singleDevicePage?.heatmap?.selectedRasterRecord);
  const deviceHeatmap = useMemo(
    () => mapRecord(selectedDeviceHeatmapRecord, isGa2 ? 'ga2' : 'ga1'),
    [selectedDeviceHeatmapRecord, isGa2]
  );
  const getRasterRecordsForDeviceStatusName = `GET-RasterRecordsForDevice-${donorDevice}`;
  const getRasterRecordsForDeviceStatus = useSelector((store) => store.statuses[getRasterRecordsForDeviceStatusName]);
  const [imageExportClick, setImageExportClick] = useState(false);
  const [isFirstLoad, setIsFistLoad] = useState(true);
  const [range, setRange] = useState(() => getDefaultHeatmapRange(isGa2 ? 'ga2' : 'ga1'));

  const numOfSelectedRasters = selectedRastersDictionary
    ? Object.values(selectedRastersDictionary).filter(Boolean).length
    : 0;

  useEffect(() => {
    // when user first visits OR its heatmap history is empty, we will fetch all of its device heatmap history
    // then grab the first raster record and its rasterId and get its heatmap
    if (
      !deviceHeatmapHistoryDictionary ||
      Object.keys(deviceHeatmapHistoryDictionary).length === 0 ||
      shouldFetchHeatmapRecords
    ) {
      fetchRasterRecordsForDevice();
      setShouldFetchHeatmapRecords(false);
    } else if (isFirstLoad) {
      let [firstRecord] = deviceHeatmapHistoryDictionary ? Object.values(deviceHeatmapHistoryDictionary) : [];
      let rasterId = firstRecord?.rasterId;
      rasterId && dispatch(updateSingleDevicePageSelectedRasterId(rasterId));
      firstRecord && dispatch(updateSingleDevicePageSelectedRasterRecord(firstRecord));
      setIsFistLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, donorDevice]);

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  const cvsDataReformatter = () => {
    if (
      !selectedRastersDictionary ||
      !deviceHeatmapHistoryDictionary ||
      Object.keys(deviceHeatmapHistoryDictionary).length === 0
    ) {
      return [];
    }

    return Object.keys(selectedRastersDictionary)
      .filter((rasterId) => selectedRastersDictionary[rasterId])
      .map((rasterId) => {
        const record = deviceHeatmapHistoryDictionary[rasterId];
        const csvData = csvDataParser(record, isGa2 ? 'ga2' : 'ga1');

        return { csvData, record };
      });
  };

  return (
    <Modal
      animateX={false}
      global
      close={onClose}
      noPadding
      overflow={true} // this makes the export dropdown visible
      className={'RemoteCommissioning_VisibleOverflow'}
      height={910}
      width={1366}
    >
      <div id="HeatmapViewAndExportModal_Container" className="HeatmapViewAndExportModal_Container">
        <div id="HeatmapViewAndExportModal_TopDiv_Text" className="HeatmapViewAndExportModal_TopDiv_Text">
          View and Export Heatmap(s)
        </div>
        <div id="HeatmapViewAndExportModal_ContentContainer" className="HeatmapViewAndExportModal_ContentContainer">
          {numOfSelectedRasters > 0 && imageExportClick && (
            <HeatmapExportableImagesContainer
              donorDevice={donorDevice}
              numOfSelectedRasters={numOfSelectedRasters}
              isGa2={isGa2}
              forMaintHeatmap={true}
            />
          )}
          <div
            id="HeatmapViewAndExportModal_HeatMapDiv"
            className={'HeatmapViewAndExportModal_HeatMapDiv'}
            style={{ width: 600 }}
          >
            <RasterHistoryHeatmap
              id={donorDevice}
              config={isGa2 ? AzElRangeGA2 : AzelRange}
              heatmap={deviceHeatmap}
              beamrank={deviceHeatmap.beamrank}
              range={range}
              isGa2={isGa2}
              forViewAndExportHeatmap={true}
              forMaintHeatmap={true}
              getRasterRecordsForDeviceStatus={getRasterRecordsForDeviceStatus}
              selectedDeviceHeatmapRecord={selectedDeviceHeatmapRecord}
              selectedDeviceHeatmapRasterId={selectedDeviceHeatmapRasterId}
            />
            <div id="HeatmapViewAndExportModal_HeatMapSliderDiv" className="HeatmapViewAndExportModal_HeatMapSliderDiv">
              <RasterHeatmapSlider
                min={deviceHeatmap.min}
                max={deviceHeatmap.max}
                value={range}
                onChange={setRange}
                heatmap={deviceHeatmap}
                forViewAndExportHeatmap={true}
              />
            </div>
          </div>
          <div
            id="HeatmapViewAndExportModal_RightInfoContainer"
            className="HeatmapViewAndExportModal_RightInfoContainer"
          >
            <div
              id="HeatmapViewAndExportModal_HeatMapLibraryTableDiv"
              className={'HeatmapViewAndExportModal_HeatMapLibraryTableDiv'}
            >
              <HeatmapLibraryTable donorDevice={donorDevice} />
            </div>
            <div className="HeatmapViewAndExportModal_Warning_Message">
              {numOfSelectedRasters >= 10 ? 'The maximum number of Heatmaps allowed to Export at one time is 10.' : ' '}
            </div>
            <div id="HeatmapViewAndExportModal_Bottom_Container" className="HeatmapViewAndExportModal_Bottom_Container">
              <div
                id="HeatmapViewAndExportModal_BottomLeft_Container"
                className="HeatmapViewAndExportModal_BottomLeft_Container"
              >
                <div
                  id="HeatmapViewAndExportModal_RetrieveButton"
                  className={classNames({
                    HeatmapViewAndExportModal_RetrieveButton: true,
                    HeatmapViewAndExportModal_RetrieveButton_Disabled: !hasCreateRasterHeatmapPermission
                  })}
                  data-tip={
                    hasCreateRasterHeatmapPermission
                      ? 'Retrieve the Latest Image from the Device'
                      : PERMISSION_ERROR_MESSAGE
                  }
                >
                  <IconAndText
                    img={IconLoader.icon_retrieve_default}
                    hoverImg={
                      hasCreateRasterHeatmapPermission
                        ? IconLoader.icon_retrieve_hover
                        : IconLoader.icon_retrieve_default
                    }
                    text={'Retrieve the Latest Image from the Device'}
                    onClick={() => {
                      hasCreateRasterHeatmapPermission && setIsHeatmapRetrieveModalOpen(true);
                    }}
                  />
                </div>
              </div>
              <div
                id="HeatmapViewAndExportModal_BottomRight_Container"
                className="HeatmapViewAndExportModal_BottomRight_Container"
              >
                <button
                  id="HeatmapViewAndExportModal_CancelButton"
                  className="HeatmapViewAndExportModal_CancelButton"
                  onClick={() => onClose()}
                  disabled={getRasterRecordsForDeviceStatus === Status.PENDING}
                >
                  Cancel
                </button>

                <HeatmapExportDropdown
                  donorDevice={donorDevice}
                  cvsDataReformatter={cvsDataReformatter}
                  setImageExportClick={setImageExportClick}
                  setIsCollectingFiles={setIsCollectingFiles}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default React.memo(HeatmapViewAndExportModal);
